import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	Input,
	OnDestroy,
	OnInit,
	Output,
	signal,
	WritableSignal,
	inject,
} from '@angular/core'
import { DatePipe } from '@angular/common'
import { Subject, takeUntil } from 'rxjs'
import { TuiIcon } from '@taiga-ui/core'
import { TuiSkeleton } from '@taiga-ui/kit'
import { TranslateService } from '@ngx-translate/core'
import { OrderClassEnum } from '@modules/orders-module/enums/order-class.enum'
import { HttpErrorResponse } from '@angular/common/http'
import { OptionOrderInterface } from '@modules/options-module/orders/interfaces/option-order.interface'
import { InfoOptionsOrderHeaderComponent } from '@shared/components/trading/orders/orders-options/tables/options/actions/info-options-order/common/info-options-order-header/info-options-order-header.component'
import { InfoOptionsOrderBodyComponent } from '@shared/components/trading/orders/orders-options/tables/options/actions/info-options-order/common/info-options-order-body/info-options-order-body.component'
import { CompanyLogoComponent } from '@shared/components/common/company-logo/company-logo.component'
import { UseLocales } from '@modules/lang-module/decorators/use-locales'
import ru from './core/locale/ru.json'
import en from './core/locale/en.json'
import { AlertService } from '@shared/services/alert.service'
import { AssetExanteInterface } from '@modules/stocks-module/general/interfaces/asset-exante.interface'
import { StocksService } from '@modules/stocks-module/general/services/stocks.service'

@UseLocales({ ru, en })
@Component({
	selector: 'app-info-options-order',
	templateUrl: './info-options-order.component.html',
	styleUrl: './info-options-order.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		TuiIcon,
		TuiSkeleton,
		DatePipe,
		InfoOptionsOrderHeaderComponent,
		InfoOptionsOrderBodyComponent,
		CompanyLogoComponent,
	],
})
export class InfoOptionsOrderComponent implements OnInit, OnDestroy {
	protected readonly orderClassEnum: typeof OrderClassEnum = OrderClassEnum

	@Input() order: OptionOrderInterface | null = null
	@Output() closeDropdown: EventEmitter<void> = new EventEmitter<void>()

	private _destroy$: Subject<void> = new Subject()

	private readonly _stocksService: StocksService = inject<StocksService>(StocksService)
	private readonly _alertService: AlertService = inject<AlertService>(AlertService)
	private readonly _translateService: TranslateService = inject<TranslateService>(TranslateService)

	protected assetsInfo: WritableSignal<AssetExanteInterface | null> = signal(null)
	protected isLoadingAssetInfo: WritableSignal<boolean> = signal(false)
	private _getAssetsInfo(): void {
		if (!this.order) return

		this.isLoadingAssetInfo.set(true)

		this._stocksService
			.getExanteAsset(this.order.option_ticker)
			.pipe(takeUntil(this._destroy$))
			.subscribe({
				next: (assets: AssetExanteInterface): void => {
					this.assetsInfo.set(assets)
					this.isLoadingAssetInfo.set(false)
				},
				error: (error: HttpErrorResponse): void => {
					this._alertService.error(
						error?.error?.error?.message || this._translateService.instant('orders.assetsInfoFailed'),
					)
					this.isLoadingAssetInfo.set(false)
				},
			})
	}

	protected onCloseDropdown(): void {
		this.closeDropdown.emit()
	}

	ngOnInit(): void {
		this._getAssetsInfo()
	}

	ngOnDestroy(): void {
		this._destroy$.next()
		this._destroy$.complete()
	}
}
